import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import { Link } from "react-router-dom";
import {
  Trophy, Star, Award, Plane, Car, Bike, Crown, Gift, ArrowRight,
  Sparkles, BadgeCheck, Medal, Ticket, HomeIcon, Rocket,
} from "lucide-react";

/**
 * Rewards and Recognition — modelled on the SmartValue-style reference:
 *   1. Page banner
 *   2. "Sweet Smell of Success" — three intro tiles (Recognition · Awards · Rewards)
 *   3. Rank ladder (graphical zig-zag)
 *   4. Per-rank benefit cards (Pin / Certificate / Memento / Listing / One-Time Gift)
 *   5. Recognition ceremonies band
 * All Samara-branded, no SmartValue text copied.
 */

const RANKS = [
  { name: "Executive",        pairs: 15,    icon: BadgeCheck, badge: "/rewards/badges/executive.jpeg",        tint: "from-blue-500 to-royal",           ring: "ring-blue-400/40",   glow: "shadow-blue-500/40",   benefits: ["Batch · Certificate · Memento", "Website Listing", "One-Time Gift / Cash Prize"] },
  { name: "Sr. Executive",    pairs: 25,    icon: Award,      badge: "/rewards/badges/senior-executive.jpeg", tint: "from-royal to-royal-deep",         ring: "ring-royal/40",      glow: "shadow-royal/40",      benefits: ["Batch · Certificate · Memento", "Website Listing", "One-Time Gift / Cash Prize"] },
  { name: "Silver",           pairs: 50,    icon: Trophy,     badge: "/rewards/badges/silver.jpeg",           tint: "from-slate-300 to-slate-500",      ring: "ring-slate-300/50",  glow: "shadow-slate-400/50",  benefits: ["Silver Pin · Certificate", "National Trip (N.T.)", "One-Time Cash Prize"] },
  { name: "Gold",             pairs: 100,   icon: Star,       badge: "/rewards/badges/gold.jpeg",             tint: "from-amber-300 to-gold-deep",      ring: "ring-amber-300/60",  glow: "shadow-amber-400/60",  benefits: ["Gold Pin · Certificate", "National Trip (N.T.)", "One-Time Cash Prize"] },
  { name: "Pearl",            pairs: 200,   icon: Sparkles,   badge: "/rewards/badges/pearl.jpeg",            tint: "from-rose-100 via-white to-rose-200", ring: "ring-rose-200/60", glow: "shadow-rose-200/50",  benefits: ["Pearl Pin · Certificate", "International Trip (I.T.)", "Car Fund"] },
  { name: "Emerald",          pairs: 400,   icon: Bike,       badge: "/rewards/badges/emerald.jpeg",          tint: "from-emerald-400 to-emerald-700",  ring: "ring-emerald-400/50",glow: "shadow-emerald-500/50",benefits: ["Emerald Pin · Certificate", "I.T.", "Car Fund", "2 × Silver in team (1:1)"] },
  { name: "Sapphire",         pairs: 600,   icon: Car,        badge: "/rewards/badges/sapphire.jpeg",         tint: "from-sky-500 to-indigo-700",       ring: "ring-sky-400/50",    glow: "shadow-sky-500/50",    benefits: ["Sapphire Pin · Certificate", "I.T.", "Car Fund", "6 × Silver in team (1:1)"] },
  { name: "Ruby",             pairs: 960,   icon: Crown,      badge: "/rewards/badges/ruby.jpeg",             tint: "from-rose-500 to-red-800",         ring: "ring-rose-400/50",   glow: "shadow-rose-500/50",   benefits: ["Ruby Pin · Certificate", "I.T.", "Car Fund", "6 × Gold in team (1:1)"] },
  { name: "Platinum",         pairs: 1560,  icon: Medal,      badge: "/rewards/badges/platinum.jpeg",         tint: "from-zinc-200 via-white to-zinc-400", ring: "ring-zinc-300/60",glow: "shadow-zinc-400/60",  benefits: ["Platinum Pin · Certificate", "I.T.", "Car Fund", "10 × Gold in team (1:1)"] },
  { name: "Diamond",          pairs: 2400,  icon: Plane,      badge: "/rewards/badges/diamond.jpeg",          tint: "from-cyan-200 via-white to-blue-300", ring: "ring-cyan-300/70",glow: "shadow-cyan-400/60",  benefits: ["Diamond Pin · Certificate", "I.T.", "Car Fund", "12 × Gold in team (1:1)"] },
  { name: "Royal",            pairs: 3900,  icon: Ticket,     badge: "/rewards/badges/royal-diamond.jpeg",    tint: "from-violet-500 to-purple-800",    ring: "ring-violet-400/50", glow: "shadow-violet-500/60", benefits: ["Royal Pin · Certificate", "I.T.", "Car Fund", "12 × Pearl in team (1:1)"] },
  { name: "Crown-Ambassador", pairs: 6000,  icon: Gift,       badge: "/rewards/badges/crown-ambassador.jpeg", tint: "from-fuchsia-500 to-purple-900",   ring: "ring-fuchsia-400/50",glow: "shadow-fuchsia-500/60",benefits: ["Crown Pin · Certificate", "I.T.", "Car Fund", "10 × Emerald in team (1:1)"] },
  { name: "Imperial-Crown",   pairs: 9000,  icon: HomeIcon,   badge: "/rewards/badges/imperial-crown.jpeg",   tint: "from-gold via-amber-500 to-purple-800", ring: "ring-gold/60", glow: "shadow-amber-500/70", benefits: ["Imperial Crown Pin", "I.T.", "Car Fund", "8 × Sapphire in team (1:1)"] },
  { name: "Global-Crown",     pairs: 14000, icon: Rocket,     badge: "/rewards/badges/global-crown.jpeg",     tint: "from-gold via-rose-400 to-royal",  ring: "ring-gold/70",       glow: "shadow-gold/70",       benefits: ["Global Crown Pin · Legacy Award", "Car Fund", "12 × Sapphire in team (1:1)"] },
];

function Trio() {
  const items = [
    {
      icon: BadgeCheck,
      l: "Recognition",
      d: "Every eligible milestone gets recognised — your name is published on the Samara Wiz website and shared with the entire associate community.",
    },
    {
      icon: Medal,
      l: "Awards",
      d: "Hard work is honoured with a signature rank pin, certificate and memento — each item carrying its own status and identity.",
    },
    {
      icon: Gift,
      l: "Rewards",
      d: "As you rise up the ranks you unlock trip funds, lifestyle rewards and the Samara Wiz Achievers Club membership.",
    },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="text-[11px] uppercase tracking-[0.4em] text-gold mb-3">The Journey</div>
        <h2 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-royal leading-tight tracking-tight">
          The sweet smell of <span className="text-gold-deep">success.</span>
        </h2>
        <p className="mt-5 text-navy/70 leading-relaxed">
          At Samara Wiz, success comes to those who — with hard work and high
          aspirations — win over every challenge on their journey. With each
          win, associates accumulate rewards and confidence to tackle the next.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((it) => (
          <div
            key={it.l}
            data-testid={`trio-${it.l.toLowerCase()}`}
            className="p-8 bg-white border border-line rounded-sm hover:border-gold/60 hover:shadow-lg transition-all group"
          >
            <div className="w-14 h-14 grid place-items-center bg-royal text-gold rounded-sm group-hover:bg-gold group-hover:text-royal transition-colors">
              <it.icon size={24} />
            </div>
            <div className="font-serif font-bold text-2xl text-royal mt-5">{it.l}</div>
            <p className="text-sm text-navy/70 mt-3 leading-relaxed">{it.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RankLadder() {
  return (
    <section
      data-testid="rank-ladder-section"
      className="bg-ivory border-y border-line py-16"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-[11px] uppercase tracking-[0.4em] text-gold mb-3">Samara Wiz Achievers Ladder</div>
        <h2 className="font-serif font-bold text-4xl sm:text-5xl text-royal leading-tight tracking-tight max-w-3xl">
          Fourteen ranks. One <span className="text-gold-deep">clear climb.</span>
        </h2>
        <p className="text-navy/60 mt-4 max-w-2xl leading-relaxed">
          Every step up the ladder is a milestone — from your first Executive
          pin all the way to Global-Crown. Below is the official Samara Wiz
          Rewards &amp; Recognition staircase.
        </p>

        <figure
          data-testid="rank-ladder"
          className="mt-10 rounded-sm overflow-hidden border border-gold/25 shadow-2xl bg-white"
        >
          <img
            src="/rewards/rewards-staircase.webp"
            alt="Samara Wiz Rewards &amp; Recognition — 14 achievement levels from Executive to Global-Crown with annual qualification pairs and team conditions."
            loading="lazy"
            className="w-full h-auto block"
          />
          <figcaption className="sr-only">
            Official Samara Wiz Rewards and Recognition staircase — 14 achievement levels.
          </figcaption>
        </figure>

        <div className="mt-4 text-[11px] text-navy/50 italic">
          Benefits are subject to eligibility verification and applicable company policy.
        </div>
      </div>
    </section>
  );
}

function BenefitDetail() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="text-[11px] uppercase tracking-[0.4em] text-gold mb-3">Reward &amp; Recognition per rank</div>
      <h2 className="font-serif font-bold text-4xl sm:text-5xl text-royal leading-tight tracking-tight max-w-3xl">
        Exactly what you <span className="text-gold-deep">unlock at each rank.</span>
      </h2>
      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {RANKS.map((r, i) => (
          <div
            key={r.name}
            data-testid={`benefit-${r.name.toLowerCase().replace(/ /g,"-")}`}
            className="p-6 bg-white border border-line rounded-sm hover:border-gold/50 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-20 h-20 sm:w-24 sm:h-24 shrink-0 grid place-items-center rounded-sm overflow-hidden border-2 border-white shadow-lg ring-2 ${r.ring} bg-gradient-to-br ${r.tint}`}>
                  <img
                    src={r.badge}
                    alt={`${r.name} official badge`}
                    loading="lazy"
                    width="192"
                    height="192"
                    className="w-full h-full object-contain drop-shadow"
                    style={{ imageRendering: "auto" }}
                  />
                </div>
                <div>
                  <div className="font-serif font-bold text-xl text-royal leading-tight">
                    {String(i + 1).padStart(2, "0")} · {r.name}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-gold-deep mt-0.5">
                    Qualification · {r.pairs.toLocaleString()} matched pairs / year
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-dashed border-line">
              <div className="text-[10px] uppercase tracking-[0.3em] text-royal font-semibold mb-2">
                Reward &amp; Recognition
              </div>
              <ul className="space-y-1.5">
                {r.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-navy/80">
                    <BadgeCheck size={14} className="text-gold-deep shrink-0 mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              {i === 0 && (
                <p className="text-[11px] text-navy/50 italic mt-3">
                  <strong>One-Time Gift:</strong> awarded exclusively to associates who reach a level for the first time.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Ceremonies() {
  return (
    <section className="bg-royal text-white py-20 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-gold/15 rounded-full blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-[11px] uppercase tracking-[0.4em] text-gold mb-3">Recognition Ceremonies</div>
        <h2 className="font-serif font-bold text-4xl sm:text-5xl leading-tight tracking-tight max-w-3xl">
          More than money — <span className="text-gold">a stage that celebrates you.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {[
            { icon: Trophy,  l: "National Achievers Meet", d: "Quarterly on-stage recognition for every rank achiever." },
            { icon: Ticket,  l: "Trip Fund Awards",         d: "Domestic and international trip rewards from Gold onwards." },
            { icon: HomeIcon,l: "Lifestyle Rewards",        d: "Bike, Car and House funds unlocked at senior ranks." },
          ].map((c) => (
            <div key={c.l} className="p-6 bg-white/[0.06] border border-white/15 rounded-sm hover:bg-white/[0.10] hover:border-gold/40 transition-all">
              <c.icon size={22} className="text-gold" />
              <div className="font-serif font-bold text-xl mt-3">{c.l}</div>
              <p className="text-xs text-white/75 mt-2 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap gap-3 items-center">
          <Link to="/plan" className="inline-flex items-center gap-2 bg-gold text-royal px-6 py-3 rounded-sm hover:bg-white transition-colors font-medium">
            See the full Business Plan <ArrowRight size={16} />
          </Link>
          <Link to="/register" className="inline-flex items-center gap-2 border border-white/60 px-6 py-3 rounded-sm hover:bg-white hover:text-royal transition-colors">
            <Rocket size={16} /> Register as an Associate
          </Link>
          <span className="text-xs text-white/50 italic">Business Plan requires associate login.</span>
        </div>
      </div>
    </section>
  );
}

export default function Rewards() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <PageBanner
        label="Rewards and Recognition"
        title="Every milestone deserves its stage."
        subtitle="Samara Wiz recognises consistent, ethical performance through a structured rank system, pins, certificates, trip funds and lifestyle rewards."
        crumbs={[{ label: "Home", to: "/" }, { label: "Smart Opportunity", to: "/opportunity" }, { label: "Rewards and Recognition" }]}
      />
      <Trio />
      <RankLadder />
      <BenefitDetail />
      <Ceremonies />
      <Footer />
    </div>
  );
}
